import React, { useCallback, useEffect, useState } from 'react';
import Vessel from './containers/vessel/Vessel';
import Summary from './containers/summary/Summary';
import Fleet from './containers/fleet/Fleet';
import { useAppSelector } from '../../core/rootHooks';
import { RootState } from '../../core/store';
import { EOutputType } from '../../shared/sharedEnums';
import { IVesselFormData } from '../../shared/sharedTypes';
import { TVesselInfo } from '../main-map/mapTypes';
import Logo from '../../shared/components/dashboard/Logo';
import GreenLogoSrc from '../../assets/images/logos/logo_green.svg';
import Coverage from '../../shared/components/dashboard/Coverage';
import { selectSummaryAvgClimateAlignment } from './greenSelector';
import { selectFleetCiiAlignment } from './greenSlice';
import { selectIsMsi } from '../../user-management/appSelectors';

const Green: React.FC = () => {
	const { outputType, selectedVesselsList } = useAppSelector((state: RootState) => state.searchSlice);
	const { vesselsLastSignalData } = useAppSelector((state: RootState) => state.vesselsLastSignalSlice);
	const summaryAvgClimateAlignment = useAppSelector(selectSummaryAvgClimateAlignment);
	const fleetCiiAlignmentData = useAppSelector(selectFleetCiiAlignment);
	const isMsi = useAppSelector(selectIsMsi);

	const [vessel, setVessel] = useState<IVesselFormData>();
	const [vessels, setVessels] = useState<IVesselFormData[]>();

	const filterGreenVesselsHaveSignal = useCallback(
		(vesselSignals: TVesselInfo, vessels?: IVesselFormData[]): IVesselFormData[] => {
			if (!vessels || vessels.length === 0 || !vesselSignals || !vesselSignals.features || vesselSignals.features.length === 0)
				return [];
			// filter green vessels that have signals by imoNumber
			const vesselsWithSignals = vessels.filter((vessel) => {
				const vesselSignal = vesselSignals!.features.find((signal) => signal.properties?.imoNumber === vessel.imoNumber);
				return !!vesselSignal;
			});

			return vesselsWithSignals;
		},
		[vesselsLastSignalData],
	);

	useEffect(() => {
		if (outputType === EOutputType.Vessel || outputType === EOutputType.Fleet) {
			const vesselsWithSignals = filterGreenVesselsHaveSignal(vesselsLastSignalData, selectedVesselsList);
			if (vesselsWithSignals && vesselsWithSignals.length > 0) {
				if (outputType === EOutputType.Vessel) {
					const vessel = vesselsWithSignals[0];
					setVessel(vessel);
				} else if (outputType === EOutputType.Fleet || outputType === EOutputType.Summary) {
					setVessels(vesselsWithSignals);
				}
			}
		} else {
			setVessels(selectedVesselsList);
		}
	}, [vesselsLastSignalData, selectedVesselsList, outputType, filterGreenVesselsHaveSignal]);

	return (
		<>
			<Logo src={GreenLogoSrc} />{' '}
			{(!vessel && (!vessels || vessels.length === 0) && <div />) || (
				<div className="flex-auto h-full w-full">
					{outputType === EOutputType.Summary && <Summary />}
					{outputType === EOutputType.Vessel && <Vessel data={vessel!} />}
					{outputType === EOutputType.Fleet && <Fleet vessels={vessels!} />}
				</div>
			)}
			{isMsi && (
				<div>
					{outputType === EOutputType.Summary && (
						<Coverage
							calculatedVesselsCount={summaryAvgClimateAlignment.calculatedImoCount}
							vesselsCount={summaryAvgClimateAlignment.totalImoCount}
							coveragePercentage={summaryAvgClimateAlignment.coveragePercentage}
						/>
					)}
					{outputType === EOutputType.Fleet && (
						<Coverage
							calculatedVesselsCount={fleetCiiAlignmentData.fleetCiiAlignmentData?.calculatedImoCount || 0}
							vesselsCount={fleetCiiAlignmentData.fleetCiiAlignmentData?.imoCount || 0}
							coveragePercentage={fleetCiiAlignmentData.fleetCiiAlignmentData?.coveragePercentage || 0}
						/>
					)}
					{outputType === EOutputType.Vessel && <Coverage calculatedVesselsCount={1} vesselsCount={1} coveragePercentage={100} />}
				</div>
			)}
		</>
	);
};

export default Green;
